import * as Sentry from '@sentry/react';
import {
  DocumentThread,
  Employee,
  EmployeeRoleLevel,
  JiraObject,
  RedactedWorkstreamMetric,
  Workstream,
  WorkstreamBasic,
  WorkstreamDependency,
  WorkstreamMetric,
  WorkstreamMetricVisibility,
  WorkstreamUpdate,
} from 'client';
import {CURRENT_QUARTER, CURRENT_YEAR, ROUTE_PATHS} from 'constant';
import {format, setDay} from 'date-fns';
import {type DateInput, formatShortDate, parseDateInput} from 'helpers/dates';
import {WorkstreamRoleId} from 'helpers/workstream_roles';
import {atomWithStorage} from 'jotai/utils';

export type WorkstreamWithHiddenMnpiMetricsCount = Workstream & {
  hidden_mnpi_metrics_count?: number | null;
};

export const isPrivateThread = (
  thread?: Pick<DocumentThread, 'private_employees' | 'private_teams'> | null
) => Boolean(thread?.private_employees?.length || thread?.private_teams?.length);

export const getWorkstreamUpdateVisualStatus = (status?: string | null) => {
  const normalizedStatus = status ?? 'no_status';
  return normalizedStatus === 'no_status' ? 'not_started' : normalizedStatus;
};

export const getWorkstreamHealthScoreUrl = (workstreamId: number | string) =>
  `${ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(workstreamId))}?showHealthScore=true`;

export const toShortDate = (date: DateInput) => formatShortDate(date);

export const toCalendarDate = (date: DateInput) => parseDateInput(date);

export function getI18nDayName(day: number) {
  const date = setDay(new Date(), day, {weekStartsOn: 1}); // Ensure Monday is 1
  return format(date, 'EEEE');
}

export const getUserType = (workstream: Workstream, employee: Employee) => {
  const isOwner = workstream.employee_associations.find(
    ({employee: {user_id}}) => user_id === employee.user_id
  );

  const isContributor = workstream.metrics.some(({owner}) => owner?.user_id === employee.user_id);

  return isOwner ? isOwner.dri : isContributor ? 'outcome-dri' : 'other';
};

export function isJiraObject(
  value: WorkstreamMetric | WorkstreamDependency | JiraObject
): value is JiraObject {
  return 'key' in value;
}

export function isWorkstreamMetric(
  value: WorkstreamMetric | WorkstreamDependency | JiraObject
): value is WorkstreamMetric {
  if (isJiraObject(value)) return false;
  return !(value as WorkstreamDependency).dependent;
}

export const isFullWorkstreamMetric = <T extends WorkstreamMetric | RedactedWorkstreamMetric>(
  metric: T
): metric is Extract<T, WorkstreamMetric> =>
  metric.visibility !== WorkstreamMetricVisibility.REDACTED;

export const getFullWorkstreamMetrics = (
  metrics: Array<WorkstreamMetric | RedactedWorkstreamMetric> | undefined | null
): WorkstreamMetric[] => (metrics ?? []).filter(isFullWorkstreamMetric);

const hasMetricForecastValue = (value?: string | null) => value != null && value.trim() !== '';

// True when the metric has a target or budget for the current quarter or current EOY.
export const metricHasCurrentTargetOrBudget = (metric: WorkstreamMetric) =>
  [`${CURRENT_YEAR}Q${CURRENT_QUARTER}`, `${CURRENT_YEAR}EOY`].some((period) => {
    const forecast = metric.metric_forecasts?.find((f) => f.period === period);
    return hasMetricForecastValue(forecast?.target) || hasMetricForecastValue(forecast?.budget);
  });

export const getWorkstreamDri = (workstream: Workstream | WorkstreamBasic | undefined) =>
  workstream?.employee_associations?.find(({dri}) => dri === 'dri')?.employee ??
  workstream?.employee_associations?.[0]?.employee;

export const getWorkstreamExecSponsor = (
  workstream: Workstream | WorkstreamBasic | undefined
): EmployeeRoleLevel | undefined =>
  workstream?.employee_associations?.find(({dri}) => dri === 'exec_sponsor')?.employee;

export const getWorkstreamWorkingGroup = (
  workstream: Workstream | WorkstreamBasic | undefined
): EmployeeRoleLevel[] => {
  const workingGroup: EmployeeRoleLevel[] = [];
  const seen = new Set<string>();

  workstream?.employee_associations?.forEach(({dri, employee}) => {
    if (dri !== 'working_group' || seen.has(employee.user_id)) return;
    seen.add(employee.user_id);
    workingGroup.push(employee);
  });

  return workingGroup;
};

export const worksteamsTableAtom = atomWithStorage('workstreams-table', {
  compact: true,
  hiddenColumns: ['notes'],
  resetColumnsTrigger: 0,
} as {
  compact: boolean;
  hiddenColumns: string[];
  resetColumnsTrigger: number;
});

export const isWorkstreamStatusClosed = (status: string) => {
  return ['complete', 'cancelled', 'missed', 'archived'].includes(status);
};

export const isWorkstreamStatusArchived = (workstream: WorkstreamBasic) => {
  return workstream.updates?.[0]?.status === 'archived';
};

type WorkstreamDependencyWithArchiveStatusAndOrder = {
  dependent: Pick<WorkstreamBasic, 'archived' | 'updates'>;
  order: number;
};

export const sortDependenciesByArchivedLastAndOrder = <
  T extends WorkstreamDependencyWithArchiveStatusAndOrder,
>(
  a: T,
  b: T
) => {
  const archivedDifference =
    Number(Boolean(a.dependent.archived) || a.dependent.updates?.[0]?.status === 'archived') -
    Number(Boolean(b.dependent.archived) || b.dependent.updates?.[0]?.status === 'archived');

  return archivedDifference || a.order - b.order;
};

export const isMetricLongterm = (metricType: string) => {
  return metricType === 'milestone' || metricType === 'metric';
};

type WorkstreamMetricWithLinkCollection = WorkstreamMetric & {
  workstream_metric_link?: {
    id?: number | null;
    workstream_id?: number | null;
    order?: number | null;
    is_primary?: boolean | null;
    created_at?: string | null;
    updated_at?: string | null;
  } | null;
  workstream_metric_links?: Array<{
    id?: number | null;
    workstream_id?: number | null;
    order?: number | null;
    is_primary?: boolean | null;
    created_at?: string | null;
    updated_at?: string | null;
  } | null> | null;
  workstream_id?: number | null;
};

export const getMetricWorkstreamLink = (metric: WorkstreamMetric, workstreamId: number) => {
  const metricWithLinkCollection = metric as WorkstreamMetricWithLinkCollection;
  const links = (metricWithLinkCollection.workstream_metric_links ?? []).filter(
    (
      link
    ): link is NonNullable<
      NonNullable<WorkstreamMetricWithLinkCollection['workstream_metric_links']>[number]
    > => Boolean(link)
  );

  const matchingLink = links.find((link) => link.workstream_id === workstreamId);
  if (matchingLink) {
    return matchingLink;
  }
  if (metricWithLinkCollection.workstream_metric_link?.workstream_id === workstreamId) {
    return metricWithLinkCollection.workstream_metric_link;
  }
  if (metricWithLinkCollection.workstream_id === workstreamId) {
    return metricWithLinkCollection.workstream_metric_link ?? null;
  }
  return null;
};

/**
 * For draft-policy "net-new" checks, prefer when this outcome was attached to the workstream
 * over the global metric `created_at` (important for linkable / cross-workstream metrics).
 */
export const getMetricCreatedAtForDraftPolicy = (
  metric: WorkstreamMetric,
  workstreamId: number
): string | null =>
  getMetricWorkstreamLink(metric, workstreamId)?.created_at ?? metric.created_at ?? null;

export const getMetricLinkedWorkstreamIds = (metric: WorkstreamMetric): number[] => {
  const metricWithLinkCollection = metric as WorkstreamMetricWithLinkCollection;
  const linkedIds = [
    ...(metricWithLinkCollection.workstream_metric_links ?? []).map((link) => link?.workstream_id),
    metricWithLinkCollection.workstream_metric_link?.workstream_id,
    metricWithLinkCollection.workstream_id,
  ];

  return Array.from(
    new Set(linkedIds.filter((id): id is number => id != null && Number.isFinite(id) && id > 0))
  ).sort((a, b) => a - b);
};

export const isClosedMetricStatus = (status: string | null | undefined) =>
  (status ?? '').trim().toLowerCase() === 'closed';

export const getMetricOrder = (metric: WorkstreamMetric, workstreamId: number) =>
  getMetricWorkstreamLink(metric, workstreamId)?.order ?? 0;

export const sortWorkstreamMetrics = (
  a: WorkstreamMetric,
  b: WorkstreamMetric,
  workstreamId: number
) => {
  const aClosed = isClosedMetricStatus(a.status);
  const bClosed = isClosedMetricStatus(b.status);
  if (aClosed !== bClosed) {
    return aClosed ? 1 : -1;
  }
  return getMetricOrder(a, workstreamId) - getMetricOrder(b, workstreamId);
};

export const isPrimaryMetric = (metric: WorkstreamMetric, workstreamId: number) =>
  Boolean(getMetricWorkstreamLink(metric, workstreamId)?.is_primary);

export type PrimaryMetricType = 'leading' | 'lagging';

export const isNorthStarMetricType = (
  metricType: WorkstreamMetric['metric_type'] | null | undefined
) => metricType === 'leading' || metricType === 'lagging';

export const getPrimaryNorthStarMetric = (metrics: WorkstreamMetric[], workstreamId: number) =>
  [...metrics]
    .filter(
      (metric) =>
        metric.type === 'metric' &&
        isNorthStarMetricType(metric.metric_type) &&
        isPrimaryMetric(metric, workstreamId)
    )
    .sort((a, b) => {
      const orderCompare = getMetricOrder(a, workstreamId) - getMetricOrder(b, workstreamId);
      if (orderCompare !== 0) {
        return orderCompare;
      }

      return a.id - b.id;
    })[0] ?? null;

export const getPrimaryMetricForWorkstreamType = (
  metrics: WorkstreamMetric[],
  workstreamId: number,
  metricType: PrimaryMetricType
) =>
  [...metrics]
    .filter(
      (metric) =>
        metric.type === 'metric' &&
        metric.metric_type === metricType &&
        isPrimaryMetric(metric, workstreamId)
    )
    .sort((a, b) => {
      const orderCompare = getMetricOrder(a, workstreamId) - getMetricOrder(b, workstreamId);
      if (orderCompare !== 0) {
        return orderCompare;
      }

      return a.id - b.id;
    })[0] ?? null;

export const getNorthStarMetricOrdering = (metrics: WorkstreamMetric[], workstreamId: number) => {
  const sortedMetrics = [...metrics].sort((a, b) => sortWorkstreamMetrics(a, b, workstreamId));
  const northStarMetric = getPrimaryNorthStarMetric(sortedMetrics, workstreamId);

  const orderedMetrics = northStarMetric
    ? [northStarMetric, ...sortedMetrics.filter((metric) => metric.id !== northStarMetric.id)]
    : sortedMetrics;

  return {northStarMetric, orderedMetrics};
};

// Status priority: active (0) > complete (1) > cancelled/missed (2) > archived (3)
export const getStatusPriority = (status?: string) => {
  if (status === 'archived') return 3;
  if (status === 'cancelled' || status === 'missed') return 2;
  if (status === 'complete') return 1;
  return 0;
};

export const sortDependenciesByStatusAndOrder = (
  a: {dependent: {updates?: WorkstreamUpdate[]}; order: number},
  b: {dependent: {updates?: WorkstreamUpdate[]}; order: number}
) => {
  const aStatusPriority = getStatusPriority(a.dependent.updates?.[0]?.status);
  const bStatusPriority = getStatusPriority(b.dependent.updates?.[0]?.status);

  // First sort by status priority
  if (aStatusPriority !== bStatusPriority) {
    return aStatusPriority - bStatusPriority;
  }

  // Then sort by order
  return a.order - b.order;
};

type RestrictedWorkstreamAccessCandidate =
  | (Pick<Workstream, 'name'> & {requested?: boolean | null})
  | (Pick<WorkstreamBasic, 'name'> & {requested?: boolean | null});

export const isRestrictedWorkstreamAccessResponse = (
  workstream: RestrictedWorkstreamAccessCandidate | undefined
): workstream is RestrictedWorkstreamAccessCandidate & {requested: boolean | null} => {
  if (!workstream) return false;
  if (workstream.requested === null || workstream.requested === undefined) return false;

  return workstream.name.trim().toLowerCase() === 'restricted';
};

export const getWorkstreamEmployeeByRole = (
  workstream: Workstream | WorkstreamBasic | undefined,
  roleId: WorkstreamRoleId
): EmployeeRoleLevel | undefined =>
  workstream?.employee_associations?.find(({dri}) => dri === roleId)?.employee;

const getSoonestMetricForecast = (metric: WorkstreamMetric) => {
  const currentPeriod = `${CURRENT_YEAR}Q${CURRENT_QUARTER}`;
  const eligibleForecasts = metric.metric_forecasts?.filter((f) => f.period >= currentPeriod);
  return eligibleForecasts?.length
    ? eligibleForecasts.reduce((soonest, forecast) =>
        forecast.period < soonest.period ? forecast : soonest
      )
    : undefined;
};

export const getSoonestMetricTarget = (metric: WorkstreamMetric): string | null => {
  const forecast = getSoonestMetricForecast(metric);

  if (forecast) {
    if (forecast.target !== metric.target) {
      Sentry.captureMessage('metric_forecast.target does not match metric.target', {
        level: 'warning',
        extra: {
          metricId: metric.id,
          period: forecast.period,
          forecastTarget: forecast.target,
          metricTarget: metric.target,
        },
      });
      return metric.target;
    }
    return forecast.target;
  }

  return metric.target;
};

export const getSoonestMetricDate = (metric: WorkstreamMetric): string | null => {
  const forecast = getSoonestMetricForecast(metric);

  if (forecast?.forecast_date) {
    return forecast.forecast_date;
  }

  if (metric.type === 'metric') {
    Sentry.captureMessage('No eligible metric_forecast found for metric', {
      level: 'warning',
      extra: {
        metricId: metric.id,
      },
    });
  }

  return metric.complete_date;
};
